import { create } from "zustand";

// Compute remaining stock from a ticket object
const getRemaining = (ticket) => {
  if (ticket.quantity_total != null && ticket.quantity_sold != null) {
    return Math.max(0, ticket.quantity_total - ticket.quantity_sold);
  }
  return 20;
};

export const useCartStore = create((set, get) => ({
  items: [], // { eventId, eventName, eventFlyer, eventDate, currency, commission, ticket, quantity }
  isDrawerOpen: false,

  openDrawer: () => set({ isDrawerOpen: true }),
  closeDrawer: () => set({ isDrawerOpen: false }),
  toggleDrawer: () => set((s) => ({ isDrawerOpen: !s.isDrawerOpen })),

  addItem: (event, ticket, quantity = 1) => {
    const { items } = get();
    const remaining = getRemaining(ticket);
    const existing = items.find(
      (i) => i.ticket.id === ticket.id && i.eventId === event.id,
    );

    if (existing) {
      const newQty = Math.min(existing.quantity + quantity, remaining);
      set({
        items: items.map((i) =>
          i.ticket.id === ticket.id && i.eventId === event.id
            ? { ...i, quantity: newQty }
            : i,
        ),
      });
    } else {
      set({
        items: [
          ...items,
          {
            eventId: event.id,
            eventName: event.name,
            eventFlyer: event.flyer_portrait,
            eventDate: event.date_start,
            currency: event.currency,
            commission: event.client_commission_percentage ?? 0,
            commissionVatIncluded: event.commission_vat_included ?? false,
            commissionVatPercentage: event.commission_vat_percentage ?? 0,
            ticket,
            quantity: Math.min(quantity, remaining),
            can_buy_without_login: event.can_buy_without_login ?? true,
            pixels: event.pixels || [],
          },
        ],
      });
    }
  },

  removeItem: (ticketId, eventId) => {
    set((s) => ({
      items: s.items.filter(
        (i) => !(i.ticket.id === ticketId && i.eventId === eventId),
      ),
    }));
  },

  updateQuantity: (ticketId, eventId, quantity) => {
    if (quantity <= 0) {
      get().removeItem(ticketId, eventId);
      return;
    }
    set((s) => ({
      items: s.items.map((i) => {
        if (i.ticket.id === ticketId && i.eventId === eventId) {
          const remaining = getRemaining(i.ticket);
          return { ...i, quantity: Math.min(quantity, remaining) };
        }
        return i;
      }),
    }));
  },

  clearCart: () => set({ items: [] }),

  // Sum of (price × qty) before commission
  getSubtotal: () =>
    get().items.reduce((sum, i) => sum + i.ticket.price * i.quantity, 0),

  // Commission amount — uses the commission % stored on the first item
  // (all items in cart are from same event, so commission is the same)
  // getCommission: () => {
  //   const { items } = get();
  //   return items.reduce((sum, i) => {
  //     const itemSubtotal = i.ticket.price * i.quantity;
  //     const rate = i.commission ?? 0;
  //     return sum + (itemSubtotal * rate) / 100;
  //   }, 0);
  // },

  // // Final total = subtotal + commission
  // getTotal: () => {
  //   const store = get();
  //   return store.getSubtotal() + store.getCommission();
  // },

  // Raw commission before VAT adjustment
  // If vat_included: VAT is inside commission, so net = commission / (1 + vat/100)
  // If not vat_included: commission is the base, VAT added on top
  getCommission: () => {
    const { items } = get();
    return items.reduce((sum, i) => {
      const itemSubtotal = i.ticket.price * i.quantity;
      const rawCommission = (itemSubtotal * (i.commission ?? 0)) / 100;
      const vatRate = (i.commissionVatPercentage ?? 0) / 100;
      const vatIncluded = i.commissionVatIncluded ?? false;

      if (vatIncluded) {
        // VAT is inside rawCommission — extract it
        // Net commission = rawCommission / (1 + vatRate)
        return sum + rawCommission / (1 + vatRate);
      } else {
        // VAT not included — commission stays as is
        return sum + rawCommission;
      }
    }, 0);
  },

  getVat: () => {
    const { items } = get();
    return items.reduce((sum, i) => {
      const itemSubtotal = i.ticket.price * i.quantity;
      const rawCommission = (itemSubtotal * (i.commission ?? 0)) / 100;
      const vatRate = (i.commissionVatPercentage ?? 0) / 100;
      const vatIncluded = i.commissionVatIncluded ?? false;

      if (vatRate === 0) return sum;

      if (vatIncluded) {
        // VAT extracted from commission: VAT = rawCommission - (rawCommission / (1 + vatRate))
        return sum + rawCommission - rawCommission / (1 + vatRate);
      } else {
        // VAT added on top of commission
        return sum + rawCommission * vatRate;
      }
    }, 0);
  },

  // Final total = subtotal + commission + vat (when not included)
  getTotal: () => {
    const store = get();
    const vatIncluded = store.items[0]?.commissionVatIncluded ?? false;
    if (vatIncluded) {
      // VAT is already inside commission so total = subtotal + rawCommission
      return (
        store.getSubtotal() +
        store.items.reduce((sum, i) => {
          const itemSubtotal = i.ticket.price * i.quantity;
          return sum + (itemSubtotal * (i.commission ?? 0)) / 100;
        }, 0)
      );
    } else {
      // VAT added on top
      return store.getSubtotal() + store.getCommission() + store.getVat();
    }
  },

  getTotalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),
}));
